import { FC, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import "../styles/SipSuccessModal.css";

interface SipSuccessModalProps {
  show: boolean;
  title?: string;
  subtitle?: string;
  duration?: number; // in ms
  redirectLabel?: string;
  onComplete: () => void;
}

const SipSuccessModal: FC<SipSuccessModalProps> = ({
  show,
  title = "SIP Activated",
  subtitle = "Your investment plan is now live.",
  duration = 10000,
  redirectLabel = "Redirecting to Passbook",
  onComplete,
}) => {
  const [remainingMs, setRemainingMs] = useState(duration);
  const circleRef = useRef<SVGCircleElement>(null);
  const checkRef = useRef<SVGPolylineElement>(null);

  // Trigger SVG animation imperatively — bypasses Bootstrap CSS interference
  useEffect(() => {
    if (!show) return;

    // Reset
    if (circleRef.current) circleRef.current.style.transform = "scale(0)";
    if (checkRef.current) checkRef.current.style.strokeDashoffset = "44";

    const t1 = setTimeout(() => {
      if (circleRef.current) circleRef.current.style.transform = "scale(1)";
    }, 100);

    const t2 = setTimeout(() => {
      if (checkRef.current) checkRef.current.style.strokeDashoffset = "0";
    }, 460);

    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [show]);

  useEffect(() => {
    if (!show) return;
    setRemainingMs(duration);
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newRemaining = duration - elapsed;
      if (newRemaining <= 0) {
        clearInterval(interval);
        setRemainingMs(0);
        onComplete();
      } else {
        setRemainingMs(newRemaining);
      }
    }, 200);
    return () => clearInterval(interval);
  }, [show, duration, onComplete]);

  const secondsLeft = Math.ceil(remainingMs / 1000);
  const progressPercentage = ((duration - remainingMs) / duration) * 100;

  return (
    <Modal
      show={show}
      centered
      backdrop="static"
      keyboard={false}
      contentClassName="sip-success-modal-pro"
    >
      <Modal.Body className="sip-success-body-pro">

        <div className="success-icon-wrapper">
          <svg
            className="success-icon"
            viewBox="0 0 64 64"
            style={{ overflow: "visible" }}
          >
            <circle
              ref={circleRef}
              cx="32"
              cy="32"
              r="32"
              fill="#16a34a"
              style={{
                transform: "scale(0)",
                transformOrigin: "32px 32px",
                transition: "transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
            />
            <polyline
              ref={checkRef}
              points="19,33 27,41 45,22"
              fill="none"
              stroke="white"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                strokeDasharray: 44,
                strokeDashoffset: 44,
                transition: "stroke-dashoffset 0.32s ease",
              }}
            />
          </svg>
        </div>

        <h5 className="success-title-pro">{title}</h5>
        <p className="success-subtitle-pro">{subtitle}</p>

        <div className="redirect-text" aria-live="polite">
          {redirectLabel} in{" "}
          <span className="countdown">{secondsLeft}s</span>
        </div>

        <div className="success-progress">
          <div
            className="success-progress-bar"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <button className="redirect-now-btn" onClick={onComplete}>
          Go to Passbook
        </button>

      </Modal.Body>
    </Modal>
  );
};

export default SipSuccessModal;